import { useState } from 'react';
import clsx from 'clsx';

import DeleteModal from '@atom/components/common/DeleteModal';
import taskTypeBlueIcon from '@atom/components/common/svgIcons/taskTypeBlue.svg';
import taskTypeBlueGreyIcon from '@atom/components/common/svgIcons/taskTypeBlueGrey.svg';
import { Icon, IconButton } from '@atom/mui';
import { DeleteIcon } from '@atom/mui/icons';
import { TaskTemplate } from '@atom/types/taskTemplate';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import classes from './taskCreate.module.css';

interface Props {
  taskTemplate: TaskTemplate;
  selectedTaskTemplate: TaskTemplate;
  setSelectedTaskTemplate: (selectedTaskTemplate: TaskTemplate) => void;
  handleDelete: (id: string) => void;
  disableDelete?: boolean;
}

const TaskTemplateRow = ({
  taskTemplate,
  selectedTaskTemplate,
  setSelectedTaskTemplate,
  handleDelete,
  disableDelete,
}: Props) => {
  const [hover, setHover] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);

  const onDelete = () => {
    handleDelete(taskTemplate?.id);
  };

  const icon = taskTemplate?.applicable
    ? taskTypeBlueIcon
    : taskTypeBlueGreyIcon;

  const subtextStyles = clsx(
    classes.templateSubtext,
    !taskTemplate?.applicable && classes.disabled,
  );

  const selected = selectedTaskTemplate?.id === taskTemplate?.id;

  const toggleTaskTemplate = () => {
    if (!taskTemplate?.applicable) {
      return null;
    }

    if (selected) {
      return setSelectedTaskTemplate(null);
    }

    return setSelectedTaskTemplate(taskTemplate);
  };

  const titleStyles = () => {
    return clsx(
      classes.templateTitle,
      !taskTemplate?.applicable && classes.disabled,
      selected && classes.selected,
    );
  };

  const rowStyles = selected
    ? classes.templateRowSelected
    : classes.templateRow;

  const showDelete =
    !disableDelete &&
    hover === taskTemplate?.id &&
    hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const getTaskTemplateSubtext = () => {
    if (taskTemplate?.rootSchemaName) {
      return taskTemplate.rootSchemaName;
    }

    if (taskTemplate?.multiAssetsEnabled) {
      return taskTemplate?.allowedMultiAssetSchemas
        .map(schema => schema?.name)
        .join(', ');
    }

    return '';
  };

  return (
    <>
      <div
        className={rowStyles}
        onMouseEnter={() => setHover(taskTemplate?.id)}
        onMouseLeave={() => setHover(null)}
        onClick={toggleTaskTemplate}
        data-cy={`taskTemplateRow-${taskTemplate.name}`}
      >
        <div className={classes.templateLabelContainer}>
          <img src={icon} />
          <div>
            <div className={titleStyles()}>{taskTemplate.name}</div>
            <div className={subtextStyles}>{getTaskTemplateSubtext()}</div>
          </div>
        </div>
        {showDelete ? (
          <IconButton
            onClick={() => setOpen(true)}
            data-cy={`taskTemplateDeleteButton-${taskTemplate.name}`}
          >
            <Icon icon={DeleteIcon} />
          </IconButton>
        ) : (
          <div className={classes.buttonSpacer} />
        )}
      </div>
      <DeleteModal
        title="Delete Task Template"
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={onDelete}
        confirmText="Delete"
        content="Are you sure you want to delete this task template?"
      />
    </>
  );
};

export default TaskTemplateRow;
